import { useQuery } from "@apollo/client";
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import Tabs from "components/ui/tabs/Tabs";
import type { ISelectedTab, ITab } from "components/ui/tabs/types";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import BrazilianIssuerDividendsQuery from "lib/queries/BrazilianIssuerDividendsQuery";
import type {
  IBrazilianIssuerDividendsQuery,
  IBrazilianIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/BrazilianIssuerDividendsQuery.generated";
import { useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import type {
  IDividendsPaidByYearDetails,
  IDividendsPaidDetails,
  IStockDividendsCorporateAction,
} from "types/graphqlTypes";
import { ISecurityCountry } from "types/graphqlTypes";
import { getYear } from "date-fns";
import parseDateYmd from "lib/utils/parseDateYmd";
import DividendsPaidTabContent from "../content/DividendsPaidTabContent";
import MandatoryMinimumDividendTabContent from "../content/MandatoryMinimumDividendTabContent";
import BrazilianStockDividendsPaidTabContet from "../content/BrazilianStockDividendsPaidTabContet";
import YearlyDividendsPaidTabContent from "../content/YearlyDividendsPaidTabContent";
import ContentContainer from "../ContentContainer";
import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import DividendsPaidSummary from "../summary/DividendsPaidSummary";
import FilterButtonTab from "./utils/FilterButtonTab";

enum DividendsTab {
  Dividends = "DIVIDENDS",
  StockDividends = "STOCK_DIVIDENDS",
  YearlyDividends = "YEARLY_DIVIDENDS",
  MandatoryMinimumDividend = "MANDATORY_MINIMUM_DIVIDEND",
}

interface IBrazilianDividendsCollapsibleProps {
  tickerSymbol: string;
  isExpanded: boolean;
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  onToggleCollapsed: () => void;
}

export default function BrazilianDividendsCollapsible({
  tickerSymbol,
  issuer,
  isExpanded,
  onToggleCollapsed,
}: IBrazilianDividendsCollapsibleProps) {
  const [filterByTickerSymbol, setFilterByTickerSymbol] = useState<string>(issuer.securities[0].tickerSymbol);
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);
  const [filterStockDividendsByYear, setFilterStockDividendsByYear] = useState<number>();

  const { data, previousData, loading } = useQuery<
    IBrazilianIssuerDividendsQuery,
    IBrazilianIssuerDividendsQueryVariables
  >(BrazilianIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Br,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  if (!data && !previousData) {
    return null;
  }

  if (!data && !previousData && loading) {
    return (
      <div className="flex w-full items-center justify-center">
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  const dividendsPaid = data && data.dividendsPaid ? data.dividendsPaid : previousData?.dividendsPaid;
  const stockDividendsPaid =
    data && data.stockDividendsPaid ? data.stockDividendsPaid : previousData?.stockDividendsPaid;

  if (!dividendsPaid) {
    return null;
  }

  const { availableYears, dividendYieldByTickerSymbol, dividendsPaidByYear, nodes } = dividendsPaid;

  const dividendsTabs: ITab[] = [
    {
      id: DividendsTab.Dividends,
      title: "Proventos",
    },
    {
      id: DividendsTab.StockDividends,
      title: "Bonificações",
      isHidden: !stockDividendsPaid || (stockDividendsPaid && stockDividendsPaid.length === 0),
    },
    {
      id: DividendsTab.YearlyDividends,
      title: (
        <div className="flex w-full items-center justify-center">
          <p className="flex sm:hidden">Anualizado</p>
          <p className="hidden sm:flex">Proventos Anualizados</p>
        </div>
      ),
    },
    {
      id: DividendsTab.MandatoryMinimumDividend,
      title: (
        <div className="flex w-full items-center justify-center">
          <p className="flex sm:hidden">Mín. Obrigatório</p>
          <p className="hidden sm:flex">Dividendo Mínimo Obrigatório</p>
        </div>
      ),
      isHidden: issuer.mandatoryMinimumDividend === null,
    },
  ];

  function getTotalCountByTypeTab(selected: ISelectedTab, tickerSymbol: string | null) {
    if (selected.id === DividendsTab.Dividends) {
      return nodes?.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
    }

    if (selected.id === DividendsTab.StockDividends) {
      return (
        stockDividendsPaid?.filter((node) => {
          if (filterActionsByTickerSymbol(tickerSymbol, node)) {
            if (!filterStockDividendsByYear) {
              return true;
            }

            if (getYear(parseDateYmd(node.excludingDate)) === filterStockDividendsByYear) {
              return true;
            }

            return false;
          }

          return false;
        }).length ?? 0
      );
    }

    return dividendsPaidByYear?.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
  }

  return (
    <CollapsibleContentSection
      title="Dividendos"
      isExpanded={isExpanded}
      isPremiumSection={true}
      onToggleCollapsed={onToggleCollapsed}
      primaryColor={palette.dixie}
      renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Proventos"]} />}
      renderContentSummary={() => (
        <DividendsPaidSummary
          dividendsPaid={nodes ?? []}
          dividendsPaidByYear={dividendsPaidByYear ?? []}
          dividendYieldSnapshotEachSymbol={dividendYieldByTickerSymbol ?? []}
        />
      )}
      renderContent={() => (
        <ContentContainer>
          <div className="p-3">
            <Tabs className="py-2" name="DividendsActions" arrayOfTabs={dividendsTabs}>
              {(selected) => (
                <div className="contents">
                  {selected.id !== DividendsTab.MandatoryMinimumDividend && issuer.securities.length > 1 && (
                    <div className="mb-2 grid w-full grid-cols-1 gap-x-3 sm:grid-cols-2">
                      {issuer.securities.map(({ tickerSymbol }) => (
                        <FilterButtonTab
                          key={`FilterTab/${tickerSymbol}`}
                          labelText={tickerSymbol}
                          totalCount={getTotalCountByTypeTab(selected, tickerSymbol)}
                          isSelected={filterByTickerSymbol === tickerSymbol}
                          onClick={() => {
                            setFilterByTickerSymbol(tickerSymbol);
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {selected.id === DividendsTab.Dividends && (
                    <DividendsPaidTabContent
                      dividendsPaid={
                        nodes?.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node)) ?? []
                      }
                      availableYears={availableYears ?? []}
                      loading={loading}
                      filterDividendsByYear={filterDividendsByYear}
                      setFilterDividendsByYear={(year) => setFilterDividendsByYear(year)}
                    />
                  )}
                  {selected.id === DividendsTab.StockDividends && (
                    <BrazilianStockDividendsPaidTabContet
                      data={
                        stockDividendsPaid?.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node)) ??
                        []
                      }
                      loading={loading}
                      filterStockDividendsByYear={filterStockDividendsByYear}
                      setFilterStockDividendsByYear={(year) => setFilterStockDividendsByYear(year)}
                    />
                  )}
                  {selected.id === DividendsTab.YearlyDividends && (
                    <YearlyDividendsPaidTabContent
                      dividendsPaidByYear={
                        dividendsPaidByYear?.filter((node) =>
                          filterActionsByTickerSymbol(filterByTickerSymbol, node),
                        ) ?? []
                      }
                      loading={loading}
                    />
                  )}
                  {selected.id === DividendsTab.MandatoryMinimumDividend && issuer.mandatoryMinimumDividend && (
                    <MandatoryMinimumDividendTabContent
                      securities={issuer.securities}
                      mandatoryMinimumDividend={issuer.mandatoryMinimumDividend}
                    />
                  )}
                </div>
              )}
            </Tabs>
          </div>
        </ContentContainer>
      )}
    />
  );
}

function filterActionsByTickerSymbol(
  tickerSymbol: string | null,
  node: IDividendsPaidDetails | IDividendsPaidByYearDetails | IStockDividendsCorporateAction,
) {
  if (tickerSymbol && tickerSymbol === node.tickerSymbol) {
    return true;
  }

  if (!tickerSymbol) {
    return true;
  }

  return false;
}
